'use client'

import { formatPrice, useCryptoPrices } from '@/hooks/use-crypto-prices'
import { useModalStore } from '@/stores/modal-store'
import { Activity, BookOpen, Headphones, Wifi } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { TradingSessionIndicator } from './trading-session-indicator'

function XIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" className={className}>
			<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
		</svg>
	)
}

export function StatusBar() {
	const [currentTime, setCurrentTime] = useState<string>('')
	const t = useTranslations('statusBar')
	const { openModal } = useModalStore()
	const { data: prices } = useCryptoPrices()

	useEffect(() => {
		const updateTime = () => {
			setCurrentTime(
				new Date().toLocaleTimeString('en-US', {
					hour: '2-digit',
					minute: '2-digit',
					second: '2-digit',
					hour12: false,
				})
			)
		}
		updateTime()
		const interval = setInterval(updateTime, 1000)
		return () => clearInterval(interval)
	}, [])

	return (
		<>
			{/* Spacer to prevent content from being hidden behind fixed footer */}
			<div className="h-7 shrink-0" />
			<footer className="scrollbar-hide fixed right-0 bottom-0 left-0 z-40 overflow-x-auto border-border border-t bg-brand-dark-light">
				<div className="flex h-7 min-w-max items-center justify-between px-4 text-xs">
					<div className="flex items-center gap-[5px] lg:gap-4">
						<div className="flex items-center gap-1.5">
							<div className="h-1.5 w-1.5 rounded-full bg-green-500" />
							<span className="text-muted-foreground">{t('connected')}</span>
						</div>
						<div className="flex items-center gap-1.5 text-muted-foreground">
							<Wifi className="h-3 w-3" />
							<span>Polygon • Solana</span>
						</div>
						<TradingSessionIndicator />
						<div className="flex items-center gap-3 font-mono text-muted-foreground">
							<span>
								BTC <span className="text-foreground">{formatPrice(prices?.BTC)}</span>
							</span>
							<span>
								ETH <span className="text-foreground">{formatPrice(prices?.ETH)}</span>
							</span>
							<span>
								SOL <span className="text-foreground">{formatPrice(prices?.SOL)}</span>
							</span>
						</div>
					</div>

					<div className="flex items-center gap-[5px] lg:gap-4">
						<div className="flex items-center gap-1.5 text-muted-foreground">
							<Activity className="h-3 w-3" />
							<span>{t('marketsLive')}</span>
						</div>
						<div className="font-mono text-muted-foreground">{currentTime}</div>
						<div className="flex items-center gap-3 text-muted-foreground">
							<button
								type="button"
								onClick={() => openModal('support')}
								className="flex items-center gap-1.5 transition-colors hover:text-foreground"
							>
								<Headphones className="h-3.5 w-3.5" />
								<span>{t('support')}</span>
							</button>
							<Link
								href="/docs"
								className="flex items-center gap-1.5 transition-colors hover:text-foreground"
							>
								<BookOpen className="h-3.5 w-3.5" />
								<span>{t('docs')}</span>
							</Link>
							<a
								href="https://x.com/onautopilotfi"
								target="_blank"
								rel="noopener noreferrer"
								className="transition-colors hover:text-foreground"
								title="X (Twitter)"
							>
								<XIcon className="h-3.5 w-3.5" />
							</a>
						</div>
					</div>
				</div>
			</footer>
		</>
	)
}
